/**
 * Form tạo/sửa BĐS dạng wizard 4 bước — dùng trong bottom sheet mobile admin.
 */
import { useMemo, useState } from 'react';
import { ButtonSpinner } from '../Common/ButtonSpinner';
import { ErrorMessage } from '../Common/ErrorMessage';
import { FormattedNumberInput } from '../Common/FormattedNumberInput';
import { ImageFileUpload } from '../Common/ImageFileUpload';
import { InputWithUnit } from '../Common/InputWithUnit';
import { AmenityToggleField } from './AmenityToggleField';
import { RoomSpecField } from './RoomSpecField';
import { PROPERTY_STATUS_OPTIONS } from '../../shared/constants/property';
import { getCategoryName } from '../../shared/utils/categoryLabel';

const SheetField = ({ label, hint, children, className = '' }) => (
  <label className={`admin-sheet-field ${className}`.trim()}>
    <span className="admin-sheet-field__label">{label}</span>
    {children}
    {hint && <small className="admin-sheet-field__hint">{hint}</small>}
  </label>
);

const CategoryChip = ({ active, children, onClick }) => (
  <button type="button" className={`admin-form-chip ${active ? 'is-active' : ''}`} onClick={onClick}>
    {children}
  </button>
);

export const AdminPropertySheetForm = ({
  formId,
  form,
  common,
  transactionType,
  propertyForm,
  categoryList,
  locationTree,
  wardList,
  isEditingProperty,
  showManagementSection,
  publicationStatusOptions,
  selectedImages,
  isSubmittingProperty,
  errorMessage,
  pendingNoticeVisible,
  roomSpecOptions,
  featureOptions,
  imageUploadConfig,
  updatePropertyField,
  selectCity,
  selectWard,
  selectPropertyImages,
  onSubmit,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [stepError, setStepError] = useState('');

  const steps = useMemo(
    () => [
      { id: 'basic', label: form.stepNavBasic },
      { id: 'pricing', label: form.stepNavPricing },
      { id: 'details', label: form.stepNavDetails },
      { id: 'publish', label: form.stepNavPublish },
    ],
    [form.stepNavBasic, form.stepNavDetails, form.stepNavPricing, form.stepNavPublish]
  );

  const lastStepIndex = steps.length - 1;
  const progress = ((activeStep + 1) / steps.length) * 100;

  const validateStep = (stepIndex) => {
    if (stepIndex === 0) {
      if (!propertyForm.category_id) {
        return form.stepErrorCategory;
      }
      if (!propertyForm.title?.trim()) {
        return form.stepErrorTitle;
      }
    }

    if (stepIndex === 1) {
      if (Number(propertyForm.price) <= 0) {
        return form.stepErrorPrice;
      }
      if (!propertyForm.province || !propertyForm.location_id) {
        return form.stepErrorLocation;
      }
    }

    return '';
  };

  const goToStep = (nextStep) => {
    setStepError('');
    setActiveStep(nextStep);
  };

  const handleNextStep = () => {
    const message = validateStep(activeStep);

    if (message) {
      setStepError(message);
      return;
    }

    goToStep(Math.min(activeStep + 1, lastStepIndex));
  };

  const handleStepClick = (stepIndex) => {
    if (stepIndex <= activeStep) {
      goToStep(stepIndex);
      return;
    }

    for (let index = 0; index < stepIndex; index += 1) {
      const message = validateStep(index);
      if (message) {
        setStepError(message);
        setActiveStep(index);
        return;
      }
    }

    goToStep(stepIndex);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStepError('');
    await onSubmit(event);
  };

  return (
    <>
      <div className="admin-form-step-chrome">
        <nav className="admin-form-step-nav" aria-label={form.stepNavLabel}>
          {steps.map((step, index) => (
            <button
              key={step.id}
              type="button"
              className={`admin-form-step-pill ${index === activeStep ? 'is-active' : ''} ${
                index < activeStep ? 'is-done' : ''
              }`.trim()}
              aria-current={index === activeStep ? 'step' : undefined}
              onClick={() => handleStepClick(index)}
            >
              <span className="admin-form-step-pill__num">{index + 1}</span>
              <span className="admin-form-step-pill__label">{step.label}</span>
            </button>
          ))}
        </nav>

        <div className="admin-form-step-progress" aria-hidden="true">
          <span style={{ width: `${progress}%` }} />
        </div>

        <p className="admin-form-step-caption">
          {form.stepProgress
            .replace('{current}', String(activeStep + 1))
            .replace('{total}', String(steps.length))}
          {' · '}
          {steps[activeStep].label}
        </p>
      </div>

      <div className="admin-form-modal-body admin-form-sheet-body">
        <form id={formId} className="property-form property-form--sheet property-form--wizard" onSubmit={handleSubmit}>
          <ErrorMessage message={errorMessage || stepError} />

          {activeStep === 0 && (
            <div className="admin-form-step-panel">
              <div className="admin-form-step-panel__head">
                <h3>{form.sectionBasicTitle}</h3>
                <span className="admin-form-transaction-badge">{transactionType.sale}</span>
              </div>

              <div className="admin-sheet-block">
                <span className="admin-sheet-block__label">{form.categoryLabel}</span>
                <div className="admin-form-chip-row">
                  {categoryList.map((category) => (
                    <CategoryChip
                      key={category.id}
                      active={String(propertyForm.category_id) === String(category.id)}
                      onClick={() => updatePropertyField('category_id', String(category.id))}
                    >
                      {getCategoryName(category)}
                    </CategoryChip>
                  ))}
                </div>
              </div>

              <SheetField label={form.titleLabel}>
                <input
                  value={propertyForm.title || ''}
                  onChange={(event) => updatePropertyField('title', event.target.value)}
                  placeholder={form.titlePlaceholder}
                  required
                />
              </SheetField>
            </div>
          )}

          {activeStep === 1 && (
            <div className="admin-form-step-panel">
              <div className="admin-form-step-panel__head">
                <h3>{form.sectionPricingTitle}</h3>
              </div>

              <div className="admin-form-field-row">
                <SheetField label={form.priceLabel}>
                  <InputWithUnit unit={form.priceUnit}>
                    <FormattedNumberInput
                      min="1"
                      value={propertyForm.price}
                      onValueChange={(numberValue) => updatePropertyField('price', numberValue)}
                      placeholder={form.pricePlaceholder}
                      required
                    />
                  </InputWithUnit>
                </SheetField>

                <SheetField label={form.areaLabel}>
                  <FormattedNumberInput
                    min="1"
                    value={propertyForm.area}
                    onValueChange={(numberValue) => updatePropertyField('area', numberValue)}
                    placeholder={form.areaPlaceholder}
                    allowDecimal
                  />
                </SheetField>
              </div>

              <div className="admin-form-field-row">
                <SheetField label={form.frontageLabel}>
                  <FormattedNumberInput
                    min="0"
                    value={propertyForm.frontage}
                    onValueChange={(numberValue) => updatePropertyField('frontage', numberValue)}
                    placeholder={form.frontagePlaceholder}
                    allowDecimal
                  />
                </SheetField>

                <SheetField label={form.plotLengthLabel} hint={form.plotLengthHint}>
                  <FormattedNumberInput
                    min="0"
                    value={propertyForm.plot_length}
                    onValueChange={() => {}}
                    placeholder={form.plotLengthPlaceholder}
                    allowDecimal
                    readOnly
                  />
                </SheetField>
              </div>

              <div className="admin-form-step-panel__head admin-form-step-panel__head--sub">
                <h3>{form.sectionLocationTitle}</h3>
              </div>

              <div className="admin-form-field-row">
                <SheetField label={form.provinceLabel}>
                  <select
                    value={propertyForm.province || ''}
                    onChange={(event) => selectCity(event.target.value)}
                    required
                  >
                    <option value="">{form.selectProvince}</option>
                    {locationTree.map((city) => (
                      <option key={city.id} value={city.name}>
                        {city.name}
                      </option>
                    ))}
                  </select>
                </SheetField>

                <SheetField label={form.wardLabel}>
                  <select
                    value={propertyForm.location_id || ''}
                    onChange={(event) => selectWard(event.target.value)}
                    disabled={!propertyForm.province}
                    required
                  >
                    <option value="">{form.selectWard}</option>
                    {wardList.map((ward) => (
                      <option key={ward.id} value={ward.id}>
                        {ward.name}
                      </option>
                    ))}
                  </select>
                </SheetField>
              </div>
            </div>
          )}

          {activeStep === 2 && (
            <div className="admin-form-step-panel">
              <div className="admin-form-step-panel__head">
                <h3>{form.sectionSpecsTitle}</h3>
              </div>

              <div className="admin-sheet-room-list">
                {roomSpecOptions.map(({ fieldName, label }) => (
                  <RoomSpecField
                    key={fieldName}
                    label={label}
                    value={propertyForm[fieldName]}
                    quantityLabel={form.roomSpecQuantityLabel}
                    onChange={(nextValue) => updatePropertyField(fieldName, nextValue)}
                  />
                ))}
              </div>

              <div className="admin-form-step-panel__head admin-form-step-panel__head--sub">
                <h3>{form.sectionAmenitiesTitle}</h3>
              </div>

              <div className="admin-sheet-amenity-grid">
                {featureOptions.map(({ fieldName, label }) => (
                  <AmenityToggleField
                    key={fieldName}
                    label={label}
                    checked={Boolean(propertyForm[fieldName])}
                    onChange={(nextValue) => updatePropertyField(fieldName, nextValue)}
                  />
                ))}
              </div>
            </div>
          )}

          {activeStep === 3 && (
            <div className="admin-form-step-panel">
              {pendingNoticeVisible && <div className="info-box">{form.pendingNotice}</div>}

              <SheetField label={common.description}>
                <textarea
                  rows="5"
                  value={propertyForm.description || ''}
                  onChange={(event) => updatePropertyField('description', event.target.value)}
                  placeholder={form.descriptionPlaceholder}
                  required
                />
              </SheetField>

              <div className="admin-sheet-block">
                <span className="admin-sheet-block__label">{form.imagesLabel}</span>
                <ImageFileUpload
                  accept={imageUploadConfig.accept}
                  hint={imageUploadConfig.hint}
                  files={selectedImages}
                  onFilesChange={selectPropertyImages}
                  disabled={isSubmittingProperty}
                />
              </div>

              {showManagementSection && (
                <>
                  <div className="admin-form-step-panel__head admin-form-step-panel__head--sub">
                    <h3>{form.sectionManagementTitle}</h3>
                  </div>

                  <div className="admin-form-field-row admin-form-field-row--single">
                    {isEditingProperty && (
                      <SheetField label={form.statusLabel}>
                        <select
                          value={propertyForm.status}
                          onChange={(event) => updatePropertyField('status', event.target.value)}
                        >
                          {PROPERTY_STATUS_OPTIONS.map((statusOption) => (
                            <option key={statusOption.value} value={statusOption.value}>
                              {statusOption.label}
                            </option>
                          ))}
                        </select>
                      </SheetField>
                    )}

                    {publicationStatusOptions.length > 0 && (
                      <SheetField label={form.publicationLabel}>
                        <select
                          value={propertyForm.publication_status}
                          onChange={(event) => updatePropertyField('publication_status', event.target.value)}
                        >
                          {publicationStatusOptions.map((publicationOption) => (
                            <option key={publicationOption.value} value={publicationOption.value}>
                              {publicationOption.label}
                            </option>
                          ))}
                        </select>
                      </SheetField>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </form>
      </div>

      <div
        className={`admin-form-sheet-footer admin-form-sheet-footer--wizard ${
          activeStep > 0 ? 'has-back' : ''
        }`.trim()}
      >
        {activeStep > 0 && (
          <button type="button" className="admin-form-sheet-back" onClick={() => goToStep(activeStep - 1)}>
            {common.back}
          </button>
        )}

        {activeStep < lastStepIndex ? (
          <button type="button" className="admin-form-sheet-next" onClick={handleNextStep}>
            {form.stepContinue}
          </button>
        ) : (
          <button
            type="submit"
            form={formId}
            className="admin-form-sheet-submit"
            disabled={isSubmittingProperty}
          >
            {isSubmittingProperty && <ButtonSpinner />}
            <span>{isSubmittingProperty ? common.saving : common.save}</span>
          </button>
        )}
      </div>
    </>
  );
};
